import React, { useMemo } from "react";
import { format, differenceInMonths, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingDown, Calculator, FileText, Download } from "lucide-react";

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const categoriaLabels = {
  veiculo: "Veículos",
  maquina_agricola: "Máquinas Agrícolas",
  implemento: "Implementos",
  equipamento: "Equipamentos",
  edificacao: "Edificações",
  terreno: "Terrenos",
  mobiliario: "Mobiliário",
  informatica: "Informática",
  outro: "Outros"
};

export default function RelatorioDepreciacao({ ativos = [] }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Calcular depreciação para cada ativo
  const ativosComDepreciacao = useMemo(() => {
    const hoje = new Date();
    
    return ativos
      .filter(a => a.status !== 'vendido' && a.status !== 'descartado')
      .map(ativo => {
        const dataAquisicao = parseISO(ativo.data_aquisicao);
        const mesesUso = differenceInMonths(hoje, dataAquisicao);
        const vidaUtil = ativo.vida_util_meses || 120;
        const valorAquisicao = ativo.valor_aquisicao || 0;
        const valorResidual = ativo.valor_residual || 0;
        const baseDepreciacao = valorAquisicao - valorResidual;
        
        let depreciacaoAcumulada = 0;
        let depreciacaoMensal = 0;
        
        if (ativo.metodo_depreciacao === 'linear' && vidaUtil > 0) {
          depreciacaoMensal = baseDepreciacao / vidaUtil;
          depreciacaoAcumulada = Math.min(depreciacaoMensal * mesesUso, baseDepreciacao);
        } else if (ativo.metodo_depreciacao === 'soma_digitos' && vidaUtil > 0) {
          // Soma dos dígitos (depreciação acelerada)
          const somaDigitos = (vidaUtil * (vidaUtil + 1)) / 2;
          const mesesRestantes = Math.max(vidaUtil - mesesUso, 0);
          depreciacaoAcumulada = baseDepreciacao * (1 - (mesesRestantes * (mesesRestantes + 1)) / (2 * somaDigitos));
        }
        
        const valorAtual = Math.max(valorAquisicao - depreciacaoAcumulada, valorResidual);
        const percentualDepreciado = valorAquisicao > 0 ? (depreciacaoAcumulada / valorAquisicao) * 100 : 0;
        const vidaUtilRestante = Math.max(vidaUtil - mesesUso, 0);
        
        return {
          ...ativo,
          mesesUso,
          depreciacaoMensal,
          depreciacaoAcumulada,
          valorAtual,
          percentualDepreciado,
          vidaUtilRestante
        };
      });
  }, [ativos]);

  // Totais
  const totais = useMemo(() => {
    return ativosComDepreciacao.reduce((acc, a) => ({
      valorAquisicao: acc.valorAquisicao + (a.valor_aquisicao || 0),
      depreciacaoAcumulada: acc.depreciacaoAcumulada + a.depreciacaoAcumulada,
      valorAtual: acc.valorAtual + a.valorAtual,
      depreciacaoMensal: acc.depreciacaoMensal + a.depreciacaoMensal
    }), { valorAquisicao: 0, depreciacaoAcumulada: 0, valorAtual: 0, depreciacaoMensal: 0 });
  }, [ativosComDepreciacao]);

  // Por categoria
  const porCategoria = useMemo(() => {
    const cats = {};
    ativosComDepreciacao.forEach(a => {
      const cat = a.categoria || 'outro';
      if (!cats[cat]) cats[cat] = { valorAquisicao: 0, valorAtual: 0, quantidade: 0 };
      cats[cat].valorAquisicao += a.valor_aquisicao || 0;
      cats[cat].valorAtual += a.valorAtual;
      cats[cat].quantidade += 1;
    });
    
    return Object.entries(cats).map(([key, val]) => ({
      categoria: categoriaLabels[key] || key,
      ...val
    })).sort((a, b) => b.valorAtual - a.valorAtual);
  }, [ativosComDepreciacao]);

  // Dados para gráfico pizza
  const dadosPizza = porCategoria.map((c, idx) => ({
    name: c.categoria,
    value: c.valorAtual,
    color: COLORS[idx % COLORS.length]
  }));

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-amber-600" />
          Relatório de Depreciação
        </h3>
        <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
          <Download className="w-4 h-4" />
          Exportar
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-xs">Valor Original</p>
            <p className="text-2xl font-bold">{formatCurrency(totais.valorAquisicao)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <p className="text-red-100 text-xs">Depreciação Acumulada</p>
            <p className="text-2xl font-bold">{formatCurrency(totais.depreciacaoAcumulada)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-green-100 text-xs">Valor Atual (Contábil)</p>
            <p className="text-2xl font-bold">{formatCurrency(totais.valorAtual)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <p className="text-amber-100 text-xs">Depreciação Mensal</p>
            <p className="text-2xl font-bold">{formatCurrency(totais.depreciacaoMensal)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Valor por Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porCategoria}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="categoria" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
                  <YAxis tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="valorAquisicao" name="Valor Original" fill="#3b82f6" />
                  <Bar dataKey="valorAtual" name="Valor Atual" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Distribuição do Patrimônio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dadosPizza}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name.substring(0, 8)} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {dadosPizza.map((entry, index) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista Detalhada */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Detalhamento por Ativo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-2 px-2">Ativo</th>
                  <th className="text-right py-2 px-2">Valor Original</th>
                  <th className="text-right py-2 px-2">Deprec. Acum.</th>
                  <th className="text-right py-2 px-2">Valor Atual</th>
                  <th className="text-center py-2 px-2">% Deprec.</th>
                  <th className="text-center py-2 px-2">Vida Útil Rest.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {ativosComDepreciacao.map(a => (
                  <tr key={a.id} className="hover:bg-stone-50">
                    <td className="py-2 px-2">
                      <p className="font-medium">{a.nome}</p>
                      <p className="text-xs text-stone-500">{categoriaLabels[a.categoria]}</p>
                    </td>
                    <td className="text-right py-2 px-2">{formatCurrency(a.valor_aquisicao)}</td>
                    <td className="text-right py-2 px-2 text-red-600">{formatCurrency(a.depreciacaoAcumulada)}</td>
                    <td className="text-right py-2 px-2 font-semibold text-green-600">{formatCurrency(a.valorAtual)}</td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        <Progress value={a.percentualDepreciado} className="h-2 flex-1" />
                        <span className="text-xs w-10">{a.percentualDepreciado.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="text-center py-2 px-2">
                      <Badge variant={a.vidaUtilRestante > 24 ? "outline" : a.vidaUtilRestante > 0 ? "secondary" : "destructive"}>
                        {a.vidaUtilRestante > 0 ? `${a.vidaUtilRestante} meses` : 'Depreciado'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-stone-50 font-semibold">
                <tr>
                  <td className="py-2 px-2">TOTAL ({ativosComDepreciacao.length} ativos)</td>
                  <td className="text-right py-2 px-2">{formatCurrency(totais.valorAquisicao)}</td>
                  <td className="text-right py-2 px-2 text-red-600">{formatCurrency(totais.depreciacaoAcumulada)}</td>
                  <td className="text-right py-2 px-2 text-green-600">{formatCurrency(totais.valorAtual)}</td>
                  <td className="text-center py-2 px-2">
                    {totais.valorAquisicao > 0 ? ((totais.depreciacaoAcumulada / totais.valorAquisicao) * 100).toFixed(0) : 0}%
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}